import { useEffect, useRef, useState, type FC, type ReactNode } from 'react'

import { CollapseIcon, ExpandIcon } from '@/components/core/Icon/icons'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'

import { OrganizationSelector } from './OrganizationSelector'
import { Index as SidebarProfileMenu } from './SidebarProfileMenu'

import './index.scss'

interface SidebarFrameProps {
  isOpen: boolean
  onToggle?: () => void
  /** Shown in the workspace selector at the top. */
  workspaceName: string
  /** The selector's dropdown, rendered while it is open; `close` shuts it. */
  renderWorkspaceDropdown: (close: () => void) => ReactNode
  /** The portal's main navigation items. */
  nav: ReactNode
  /** The portal's items above the profile menu. */
  bottomItems: ReactNode
  userEmail: string
  onLogout: () => void
  /** Opens the account page; without it the profile menu has no "My account". */
  onMyAccount?: () => void
  termsOfServicePath: string
}

/**
 * The sidebar both portals share: the workspace selector and its dropdown, the collapse toggle and the profile menu
 * with sign out. Each portal fills in its own workspaces and navigation: `Sidebar` for the original portal,
 * `B2bSidebar` for the B2B one.
 */
export const SidebarFrame: FC<SidebarFrameProps> = ({
  isOpen,
  onToggle,
  workspaceName,
  renderWorkspaceDropdown,
  nav,
  bottomItems,
  userEmail,
  onLogout,
  onMyAccount,
  termsOfServicePath,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement
        // Don't close if clicking on the selector itself
        if (!target.closest('.sidebar__org-selector')) {
          setIsDropdownOpen(false)
        }
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  return (
    <div className={`sidebar ${isOpen ? 'sidebar--open' : 'sidebar--collapsed'}`}>
      <div className="sidebar__content">
        <div className="sidebar__org-header" ref={dropdownRef} data-testid={SIDEBAR_TEST_IDS.ORGANIZATION_HEADER}>
          <OrganizationSelector
            organizationName={workspaceName}
            isOpen={isDropdownOpen}
            isCollapsed={!isOpen}
            onClick={() => setIsDropdownOpen(prev => !prev)}
          />
          {isDropdownOpen && renderWorkspaceDropdown(() => setIsDropdownOpen(false))}
        </div>

        <div className="sidebar__nav">{nav}</div>

        {/* Flex spacer to push bottom section down */}
        <div style={{ flex: 1 }} />

        <div className="sidebar__bottom">
          {bottomItems}
          <SidebarProfileMenu
            userEmail={userEmail}
            isSidebarOpen={isOpen}
            onLogout={onLogout}
            onMyAccount={onMyAccount}
            termsOfServicePath={termsOfServicePath}
          />
        </div>
      </div>

      {onToggle && (
        <button
          type="button"
          className="sidebar__toggle"
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={onToggle}
          title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          data-testid={SIDEBAR_TEST_IDS.TOGGLE}
        >
          {isOpen ? <CollapseIcon /> : <ExpandIcon />}
        </button>
      )}
    </div>
  )
}
